import curry from 'lodash/curry'
import React, { CSSProperties, ReactElement, useEffect, useMemo, useRef } from 'react'
import { useState } from 'react'
import styled from 'styled-components'
import { randomNormal } from '../util/maths'

const Token = styled.span<{
    colour: string
}>`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 1em;
    color: ${({ colour }) => colour};
    margin-right: 1em;
`

export const RedactedText = (
    colour: string, 
    {children, style}: {
        children: string
        style?: CSSProperties
    }
) => {
    return (
        <div
            style={{
                width: `${children.length}em`,
                height: `1em`,
                backgroundColor: colour,
                marginRight: '1em',
                position: 'relative',
                top: '1px',
                ...style
            }}
        />
    )
}

export const CodeToken = (colour: string, {children, style}: {
    children: string
    style?: CSSProperties
}) => 
    <Token style={style} colour={colour}>{children}</Token>

const LineWrapper = styled.div`
    display: flex;
    align-items: center;
`

type CodeBlockArgs = {
    startCharacter: number
    averageTypeSpeed: number
    typeSpeedStandardDeviation: number
    backwards?: boolean
    endCharacter?: number
    onCharacterChange?: (characters: number) => void
}

export function CodeBlock({
        startCharacter,
        averageTypeSpeed,
        typeSpeedStandardDeviation,
        backwards,
        endCharacter,
        onCharacterChange
    }: CodeBlockArgs,
    { 
        children,
        style
    }: 
    { 
        children: React.ReactNode
        style?: CSSProperties
    }
){
    
    const totalCharacters = useMemo(() => 
        
        React.Children.toArray(children).reduce((acc, current: any) => 
            current.type === 'br' ? acc : acc + current.props.children.length
        , 0) as number

    , [children])

    const [characters, setCharacters] = useState<number>(
        backwards ? 
            Math.min(endCharacter ?? totalCharacters, totalCharacters) : 
            Math.min(startCharacter, totalCharacters)
    )
    const backspacedPreviously = useRef(false);

    const updateCharacters = useMemo(() => (characters: number) => {
        setCharacters(characters)
        onCharacterChange && onCharacterChange(characters)
    }, [onCharacterChange])

    const groupedRenderedChildren = useMemo(() => {

        let renderedCharacters = 0
        const groups: Array<Array<ReactElement>> = [[]]
        const childArray = React.Children.toArray(children)

        for(let i = 0; renderedCharacters < characters; i++){

            const child = childArray[i]
            if(!React.isValidElement(child) ) continue
            if(child.type === 'br'){
                if(groups[groups.length - 1].length === 0)
                    groups[groups.length - 1].push(<div style={{height: '1em'}}/>)
                groups.push([])
                continue
            }

            const content = child.props.children
            renderedCharacters = renderedCharacters + content.length

            renderedCharacters < characters ?
                groups[groups.length - 1].push(child) : 
                groups[groups.length - 1].push(
                    React.cloneElement(
                        child as ReactElement,
                        {
                            children: content.substring(0, content.length - (renderedCharacters - characters))
                        }
                    )
                )
        }

        return groups

    }, [characters, children])

    useEffect(() => {
        let backspace = backspacedPreviously.current ? 
            Math.random() < 0.6 : 
            Math.random() < 0.03

        if(backwards) backspace = false
        if(backspace) backspacedPreviously.current = true
        else backspacedPreviously.current = false

        const waitTime = Math.max(30, randomNormal(averageTypeSpeed, typeSpeedStandardDeviation))
        
        if(
            ((characters === totalCharacters || endCharacter) && !backwards) ||
            ((characters === 0 || characters === startCharacter) && backwards)
        ) 
            return

        if(!backwards)
            setTimeout(() => updateCharacters(backspace ? characters - 1 : characters + 1), waitTime)
        else
            setTimeout(() => updateCharacters(characters - 1), waitTime)
    }, [averageTypeSpeed, backwards, characters, endCharacter, startCharacter, totalCharacters, typeSpeedStandardDeviation, updateCharacters])

    return (
        <div style={style}>
            {
                groupedRenderedChildren.map( (grouping) => 
                    <LineWrapper>
                        {grouping}
                    </LineWrapper>
                )
            }
        </div>
    )
}

//TODO remove this weird function..
export function createAnimatedCodeBlock(
    commentColour: string,
    keywordColour: string,
    variableColour: string,
    operatorColour: string,
    templateParamColor: string,
    typenameColor: string,
    startCharacter = 20,
    averageTypeSpeed = 200,
    typeSpeedStandardDeviation = 200,
    onCharacterChange: (characters: number) => void,
    style?: CSSProperties
){  
    return (
        {

            Comment: curry(CodeToken)(commentColour),
            Keyword: curry(CodeToken)(keywordColour),
            Variable: curry(CodeToken)(variableColour),
            Operator: curry(CodeToken)(operatorColour),
            TemplateParam: curry(CodeToken)(templateParamColor),
            Typename: curry(CodeToken)(templateParamColor),

            RedactedComment: curry(RedactedText)(commentColour),
            RedactedKeyword: curry(RedactedText)(keywordColour),
            RedactedVariable: curry(RedactedText)(variableColour),
            RedactedOperator: curry(RedactedText)(operatorColour),
            RedactedTemplateParam: curry(RedactedText)(templateParamColor),
            RedactedTypename: curry(RedactedText)(typenameColor),

            CodeBlock: curry(CodeBlock)({
                startCharacter: startCharacter,
                averageTypeSpeed: averageTypeSpeed,
                typeSpeedStandardDeviation: typeSpeedStandardDeviation,
                onCharacterChange: onCharacterChange
            })
        }
    )
}